import { StepBasedForm, SuccessPage } from '@components'

import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { SkatIntegrationStatus } from '@features/vatDeclarations/enums/skatIntegrationStatus'

import { useTrackVatDeclarationEvent } from '../../../../hooks/useTrackVatDeclarationEvent'
import { useSubmitToSkat } from './context/submitToSkatContext'
import { InstructionsPage } from './elements/InstructionsPage'

interface SubmitToSkatFormProps {
  setIsSubmissionFormOpen: (isOpen: boolean) => void
}

const stepStatus = {
  [SkatIntegrationStatus.Idle]: '1 - Transfer to',
  [SkatIntegrationStatus.Redirecting]: '2 - Redirecting',
  [SkatIntegrationStatus.Connecting]: '3 - Connecting',
}

export const SubmitToSkatForm = ({ setIsSubmissionFormOpen }: SubmitToSkatFormProps) => {
  const { t } = useTranslation()
  const { connectionState, isAcceptedBySkat, setConnectionState } = useSubmitToSkat()
  const trackEvent = useTrackVatDeclarationEvent()

  const handleClose = useCallback(() => {
    setConnectionState(SkatIntegrationStatus.Idle)
    setIsSubmissionFormOpen(false)
  }, [setConnectionState, setIsSubmissionFormOpen])

  const handleDoneClick = useCallback(() => {
    handleClose()
    trackEvent('xxx - Bookkeeping - VAT - Submit VAT DK', {
      flow: 'Success',
      step: '4 - Success',
      buttonClick: 'Done',
    })
  }, [handleClose, trackEvent])

  const handleCancelClick = useCallback(() => {
    handleClose()
    trackEvent('xxx - Bookkeeping - VAT - Submit VAT DK', {
      step: stepStatus[connectionState],
      buttonClick: 'Cancel',
    })
  }, [connectionState, handleClose, trackEvent])

  const handleBackClick = useCallback(() => {
    handleClose()
    trackEvent('xxx - Bookkeeping - VAT - Submit VAT DK', {
      step: !isAcceptedBySkat ? stepStatus[connectionState] : '4 - Success',
      buttonClick: 'Back',
    })
  }, [connectionState, handleClose, isAcceptedBySkat, trackEvent])

  return (
    <StepBasedForm onBack={handleBackClick}>
      {isAcceptedBySkat ? (
        <SuccessPage
          onDoneClick={handleDoneClick}
          headerLabel={t('skat.submit_form.success_header')}
          bodyLabel={t('skat.submit_form.success_body')}
        />
      ) : (
        <InstructionsPage handleCancelClick={handleCancelClick} />
      )}
    </StepBasedForm>
  )
}
